import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  X,
  CreditCard,
  Phone,
  CheckCircle,
  AlertCircle,
  Clock,
  Shield,
} from "lucide-react";
import {
  initiateStudentMpesaPayment,
  checkPaymentStatus,
} from "../../services/fees";
import { toast } from "react-hot-toast";

interface MpesaPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  maxAmount: number;
  studentInfo: {
    name: string;
    studentId: string;
  };
  onPaymentSuccess: () => void;
}

const MpesaPaymentModal: React.FC<MpesaPaymentModalProps> = ({
  isOpen,
  onClose,
  maxAmount,
  studentInfo,
  onPaymentSuccess,
}) => {
  const [step, setStep] = useState<
    "amount" | "phone" | "processing" | "pin" | "success" | "failed"
  >("amount");
  const [amount, setAmount] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [paymentId, setPaymentId] = useState<string>("");
  const [countdown, setCountdown] = useState<number>(0);
  const [checkInterval, setCheckInterval] = useState<NodeJS.Timeout | null>(
    null
  );

  // Format phone number as user types
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Handle different input formats
    if (digits.startsWith("254")) {
      return digits.slice(0, 12); // 254XXXXXXXXX
    } else if (digits.startsWith("0")) {
      return "254" + digits.slice(1, 10); // 0XXXXXXXXX -> 254XXXXXXXXX
    } else if (digits.length <= 9) {
      return "254" + digits; // XXXXXXXXX -> 254XXXXXXXXX
    }

    return digits.slice(0, 12);
  };

  const formatDisplayPhone = (phone: string) => {
    if (phone.length === 12 && phone.startsWith("254")) {
      return `+254 ${phone.slice(3, 6)} ${phone.slice(6, 9)} ${phone.slice(9)}`;
    }
    return phone;
  };

  // Initiate Mpesa payment
  const initiateMutation = useMutation({
    mutationFn: initiateStudentMpesaPayment,
    onSuccess: (response: any) => {
      console.log("Payment initiated:", response);
      // The backend returns data directly, not wrapped in response.data
      setPaymentId(response.paymentId);
      setStep("pin");
      setCountdown(120); // 2 minutes for PIN entry

      // Start checking payment status
      startStatusCheck(response.paymentId);

      toast.success("Payment request sent! Please check your phone.");
    },
    onError: (error: any) => {
      console.error("Payment initiation failed:", error);
      setStep("failed");
      toast.error(error.response?.data?.message || "Payment initiation failed");
    },
  });

  // Check payment status
  const statusQuery = useQuery({
    queryKey: ["payment-status", paymentId],
    queryFn: () => checkPaymentStatus(paymentId),
    enabled: false, // We'll enable this manually
    refetchInterval: 3000, // Check every 3 seconds
  });

  const startStatusCheck = (id: string) => {
    setPaymentId(id);

    // Clear any existing interval
    if (checkInterval) {
      clearInterval(checkInterval);
    }

    // Start new interval for status checking
    const newInterval = setInterval(async () => {
      try {
        const response = await checkPaymentStatus(id);
        console.log("Payment status response:", response);

        // Handle both response formats: {success, data} or direct payment object
        const payment = response.data || response;
        console.log("Payment data:", payment);

        if (payment && payment.status === "completed") {
          setStep("success");
          clearInterval(newInterval);
          setCheckInterval(null);
          toast.success("Payment completed successfully!");
          setTimeout(() => {
            onPaymentSuccess();
            onClose();
          }, 2000);
        } else if (payment && payment.status === "failed") {
          setStep("failed");
          clearInterval(newInterval);
          setCheckInterval(null);
          toast.error("Payment failed. Please try again.");
        }
        // If status is still "pending", continue checking
      } catch (error) {
        console.error("Status check error:", error);
      }
    }, 3000);

    setCheckInterval(newInterval);

    // Clear interval after 5 minutes to prevent infinite checking
    setTimeout(() => {
      clearInterval(newInterval);
      setCheckInterval(null);
      if (step === "pin") {
        setStep("failed");
        toast.error("Payment timeout. Please try again.");
      }
    }, 300000); // 5 minutes
  };

  // Cleanup function to clear interval
  const clearStatusCheck = () => {
    if (checkInterval) {
      clearInterval(checkInterval);
      setCheckInterval(null);
    }
  };

  // Cleanup when component unmounts or modal closes
  useEffect(() => {
    return () => {
      clearStatusCheck();
    };
  }, [checkInterval]);

  // Clear interval when modal closes
  useEffect(() => {
    if (!isOpen) {
      clearStatusCheck();
    }
  }, [isOpen]);

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0 && (step === "pin" || step === "processing")) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && step === "pin") {
      setStep("failed");
      toast.error("Payment timeout. Please try again.");
    }
  }, [countdown, step]);

  const handleAmountSubmit = () => {
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (numAmount > maxAmount) {
      toast.error(`Amount cannot exceed KSh ${maxAmount.toLocaleString()}`);
      return;
    }
    setStep("phone");
  };

  const handlePhoneSubmit = () => {
    if (phoneNumber.length !== 12 || !phoneNumber.startsWith("254")) {
      toast.error("Please enter a valid Kenyan phone number");
      return;
    }

    setStep("processing");
    initiateMutation.mutate({
      amount: parseFloat(amount),
      phoneNumber,
    });
  };

  const resetModal = () => {
    clearStatusCheck(); // Clear any running payment status checks
    setStep("amount");
    setAmount("");
    setPhoneNumber("");
    setPaymentId("");
    setCountdown(0);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                M-Pesa Payment
              </h3>
              <p className="text-sm text-gray-500">Pay with M-Pesa</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Student Info */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-blue-900">{studentInfo.name}</p>
                <p className="text-sm text-blue-700">
                  ID: {studentInfo.studentId}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-700">Outstanding Balance</p>
                <p className="font-semibold text-blue-900">
                  KSh {maxAmount.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Step 1: Amount Input */}
          {step === "amount" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"></span>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    max={maxAmount}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0.00"
                    autoFocus
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Maximum: KSh {maxAmount.toLocaleString()}
                </p>
              </div>

              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-3 gap-2">
                {[1000, 5000, 10000].map((quickAmount) => (
                  <button
                    key={quickAmount}
                    onClick={() => setAmount(quickAmount.toString())}
                    className="py-2 px-3 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={quickAmount > maxAmount}
                  >
                    {quickAmount.toLocaleString()}
                  </button>
                ))}
              </div>

              <button
                onClick={handleAmountSubmit}
                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Phone Number Input */}
          {step === "phone" && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <p className="text-lg font-medium text-gray-900">
                  KSh {parseFloat(amount).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">Payment Amount</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  M-Pesa Phone Number
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    value={formatDisplayPhone(phoneNumber)}
                    onChange={(e) =>
                      setPhoneNumber(formatPhoneNumber(e.target.value))
                    }
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="+254 7XX XXX XXX"
                    autoFocus
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Enter the M-Pesa number to receive the payment prompt
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setStep("amount")}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handlePhoneSubmit}
                  className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Pay Now
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Processing */}
          {step === "processing" && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <Clock className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">
                  Processing Payment
                </h4>
                <p className="text-gray-600">
                  Initiating M-Pesa payment request...
                </p>
              </div>
            </div>
          )}

          {/* Step 4: PIN Entry */}
          {step === "pin" && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">
                  Enter Your M-Pesa PIN
                </h4>
                <p className="text-gray-600">
                  A payment request has been sent to{" "}
                  <span className="font-medium">
                    {formatDisplayPhone(phoneNumber)}
                  </span>
                </p>
                <p className="text-sm text-blue-600 font-medium">
                  Amount: KSh {parseFloat(amount).toLocaleString()}
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  1. Check your phone for the M-Pesa prompt
                  <br />
                  2. Enter your M-Pesa PIN
                  <br />
                  3. Confirm the payment
                </p>
              </div>

              {countdown > 0 && (
                <div className="text-sm text-gray-500">
                  Request expires in: {Math.floor(countdown / 60)}:
                  {(countdown % 60).toString().padStart(2, "0")}
                </div>
              )}

              <button
                onClick={handleClose}
                className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Step 5: Success */}
          {step === "success" && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">
                  Payment Successful!
                </h4>
                <p className="text-gray-600">
                  Your payment of KSh {parseFloat(amount).toLocaleString()} has
                  been processed successfully.
                </p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  You will receive an M-Pesa confirmation message shortly.
                </p>
              </div>
            </div>
          )}

          {/* Step 6: Failed */}
          {step === "failed" && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">
                  Payment Failed
                </h4>
                <p className="text-gray-600">
                  Your payment could not be processed. Please try again.
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleClose}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    resetModal();
                    setStep("amount");
                  }}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MpesaPaymentModal;
